import {
  Button,
  Chip,
  Divider,
  IconButton,
  Snackbar,
  Tooltip,
  Typography
} from '@material-ui/core';
import { FileCopyOutlined, GetApp, Visibility, VisibilityOff } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import AdminDialog from 'components/admin-dialog';
import React, { useMemo, useState } from 'react';
import './api-access-section.scss';
import type { CompanyApiAccess } from './company-api-access-type';

type ApiAccessSectionProps = {
  apiAccess: CompanyApiAccess[];
  isLoading?: boolean;
  isSaving?: boolean;
  onGenerateKey: () => Promise<void> | void;
  onRotateKey: () => Promise<void> | void;
  onExpireKey: () => Promise<void> | void;
  onRevokeKey: () => Promise<void> | void;
};

const formatDate = (value?: string | null) => {
  if (!value) {
    return '—';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '—';
  }

  const parts = new Intl.DateTimeFormat("en-AU", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(parsed);

  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? "";

  return `${get("day")}/${get("month")}/${get("year")}, ${get("hour")}:${get("minute")} ${get("dayPeriod").toLowerCase()}`;
};

const getStatusColor = (status: CompanyApiAccess['status']): 'default' | 'primary' | 'secondary' => {
  if (status === 'active') return 'primary';
  if (status === 'revoked' || status === 'expired') return 'secondary';
  return 'default';
};

const maskApiKey = (apiKey: string) => {
  if (apiKey.length <= 8) {
    return '••••••••';
  }

  return `${apiKey.slice(0, 4)}••••••••••${apiKey.slice(-4)}`;
};

const getStatusLabel = (status: CompanyApiAccess['status']) => {
  switch (status) {
    case 'active':
      return 'Active';
    case 'revoked':
      return 'Revoked';
    case 'expired':
      return 'Expired';
    default:
      return 'No key';
  }
};

const API_POSTMAN_PACK_ZIP = '/documents/sheep-genetics-api-postman-pack.zip';

export const ApiAccessSection: React.FC<ApiAccessSectionProps> = ({
  apiAccess,
  isLoading = false,
  isSaving = false,
  onGenerateKey,
  onRotateKey,
  onExpireKey,
  onRevokeKey,
}) => {
  const [revealedKeys, setRevealedKeys] = useState<Record<string, boolean>>({});
  const [isCopySnackbarOpen, setIsCopySnackbarOpen] = useState(false);
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);
  const [showRotateDialog, setShowRotateDialog] = useState(false);

  const apiAccessRows = useMemo(() => apiAccess.length ? apiAccess : [], [apiAccess]);
  const hasNoApiKey = apiAccessRows.every((access) => access.status === 'none' || !access.apiKey);
  const hasExpiredApiKey = apiAccessRows.some((access) => access.status === 'expired');
  const getRowKey = (access: CompanyApiAccess, index: number) =>
    `${access.id ?? access.companyId}-${access.createdAt ?? 'none'}-${index}`;

  const handleCopy = async (apiKey: string | null) => {
    if (!apiKey) {
      return;
    }

    try {
      await navigator.clipboard.writeText(apiKey);
      setIsCopySnackbarOpen(true);
    } catch (error) {
      console.error('Unable to copy API key to clipboard.', error);
    }
  };

  const handleRequestRevoke = () => {
    setShowRevokeDialog(true);
  };

  const handleRequestRotate = () => {
    setShowRotateDialog(true);
  };

  const handleCancelRevoke = () => {
    setShowRevokeDialog(false);
  };

  const handleCancelRotate = () => {
    setShowRotateDialog(false);
  };

  const handleConfirmRevoke = () => {
    setShowRevokeDialog(false);
    setRevealedKeys({});
    void onRevokeKey();
  };

  const handleExpireKey = () => {
    void onExpireKey();
  };

  const handleConfirmRotate = () => {
    setShowRotateDialog(false);
    setRevealedKeys({});
    void onRotateKey();
  };

  const handleCloseCopySnackbar = (
    event?: React.SyntheticEvent,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setIsCopySnackbarOpen(false);
  };

  return (
    <section className='manage-company-api-access'>
      <Alert severity='info' className='manage-company-api-access__instructions-panel'>
        <div className='manage-company-api-access__instructions-content'>
          <div>
            <Typography variant='subtitle2' className='manage-company-api-access__instructions-title'>
              Sheep Genetics API access resources
            </Typography>
            <Typography variant='body2' className='manage-company-api-access__instructions-copy'>
              Sheep Genetics API access supports genomic reporting services, including GBV and Flock Profile data. Download the pack for the Postman collection, empty Postman environment, and the third-party API reference document with endpoint details and setup instructions.
            </Typography>
            <Typography variant='body2' className='manage-company-api-access__instructions-copy'>
              API keys are masked by default. Share the key only with your authorised provider, keep it secure, and contact Sheep Genetics if the key needs to be changed.
            </Typography>
          </div>
          <Button
            component='a'
            href={API_POSTMAN_PACK_ZIP}
            download
            variant='contained'
            color='primary'
            startIcon={<GetApp fontSize='small' />}
            className='manage-company-api-access__instructions-download'
          >
            Download API Pack
          </Button>
        </div>
      </Alert>
      <Divider className='manage-company-api-access__divider' />

      {!isLoading && hasNoApiKey && (
        <Alert severity='info' className='manage-company-api-access__message'>
          No API key has been generated for this company yet.
        </Alert>
      )}

      {hasExpiredApiKey && (
        <Alert severity='warning' className='manage-company-api-access__message'>
          One or more API keys have expired. Generate a new key to continue API access.
        </Alert>
      )}

      {isLoading ? (
        <Alert severity='info' className='manage-company-api-access__message'>
          Loading API access settings...
        </Alert>
      ) : (
        <div className='manage-company-api-access__table-wrap'>
        <div className='manage-company-api-access__table-header'>
          <Typography variant='caption'>API Key</Typography>
          <Typography variant='caption'>Status</Typography>
          <Typography variant='caption'>Created</Typography>
          {/* <Typography variant='caption'>Last Rotated</Typography> */}
          <Typography variant='caption'>Expires</Typography>
          {/* <Typography variant='caption'>Actions</Typography> */}
        </div>

        {apiAccessRows.map((access, index) => {
          const rowKey = getRowKey(access, index);
          const revealKey = Boolean(revealedKeys[rowKey]);
          const displayKey = access.apiKey
            ? (revealKey ? access.apiKey : maskApiKey(access.apiKey))
            : '';

          return (
            <div className='manage-company-api-access__table-row' key={rowKey}>
              <div className='manage-company-api-access__cell manage-company-api-access__cell--key'>
                <Typography variant='caption' className='manage-company-api-access__cell-label'>API Key</Typography>
                <div className='manage-company-api-access__key-display' aria-live='polite'>
                  <Typography variant='body2' className='manage-company-api-access__key-value'>
                    {displayKey || (access.status === 'none' ? 'No key generated' : 'Key unavailable')}
                  </Typography>
                  <div className='manage-company-api-access__key-actions'>
                    <Tooltip title={revealKey ? 'Hide key' : 'Reveal key'}>
                      <span>
                        <IconButton
                          size='small'
                          onClick={() => setRevealedKeys((previous) => ({
                            ...previous,
                            [rowKey]: !previous[rowKey],
                          }))}
                          disabled={!access.apiKey}
                          className='manage-company-api-access__key-action'
                        >
                          {revealKey ? <VisibilityOff fontSize='small' /> : <Visibility fontSize='small' />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title='Copy key'>
                      <span>
                        <IconButton
                          size='small'
                          onClick={() => handleCopy(access.apiKey)}
                          disabled={!access.apiKey}
                          className='manage-company-api-access__key-action'
                        >
                          <FileCopyOutlined fontSize='small' />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </div>
                </div>
              </div>

              <div className='manage-company-api-access__cell'>
                <Typography variant='caption' className='manage-company-api-access__cell-label'>Status</Typography>
                <Chip
                  size='small'
                  color={getStatusColor(access.status)}
                  label={getStatusLabel(access.status)}
                  className='manage-company-api-access__status'
                />
              </div>

              <div className='manage-company-api-access__cell'>
                <Typography variant='caption' className='manage-company-api-access__cell-label'>Created</Typography>
                <Typography variant='body2' className='manage-company-api-access__meta-value date'>{formatDate(access.createdAt)}</Typography>
              </div>

              <div className='manage-company-api-access__cell'>
                <Typography variant='caption' className='manage-company-api-access__cell-label'>Expires</Typography>
                <Typography variant='body2' className='manage-company-api-access__meta-value date'>{formatDate(access.expiresAt)}</Typography>
              </div>

          {/* <div className='manage-company-api-access__cell manage-company-api-access__cell--actions'>
            <Typography variant='caption' className='manage-company-api-access__cell-label'>Actions</Typography>
            <div className='manage-company-api-access__actions'>
              {apiAccess.status === 'active' ? (
                <>
                <Button
                  variant='text'
                  color='primary'
                  className="small"
                  disabled={isLoading || isSaving || !apiAccess.apiKey}
                  onClick={handleRequestRotate}
                  startIcon={<Autorenew fontSize='small' />}
                >
                  Rotate Key
                </Button>
                <Button
                  variant='text'
                  color='secondary'
                  className="small"
                  disabled={isLoading || isSaving || !apiAccess.apiKey}
                  onClick={handleRequestRevoke}
                  startIcon={<Close fontSize='small' />}
                >
                  Revoke Key
                </Button>
                </>
              ) : (
                <Button
                  variant='text'
                  color='primary'
                  disabled={isLoading || isSaving}
                  onClick={() => {
                    void onGenerateKey();
                  }}
                  startIcon={<Add fontSize='small' />}
                >
                  Generate Key
                </Button>
              )}
            </div>
          </div> */}
            </div>
          );
        })}
        </div>
      )}

      <AdminDialog
        open={showRotateDialog}
        title='Rotate API Key'
        labelYes='Rotate Key'
        labelNo='Cancel'
        onYes={handleConfirmRotate}
        onNo={handleCancelRotate}
        onClose={handleCancelRotate}
        isLoading={isSaving}
      >
        Rotating this API key will immediately replace the current key. Any existing integrations using the
        current key may stop working until they are updated with the new key.
      </AdminDialog>

      <AdminDialog
        open={showRevokeDialog}
        title='Revoke API Key'
        labelYes='Confirm Revoke'
        labelNo='Cancel'
        onYes={handleConfirmRevoke}
        onNo={handleCancelRevoke}
        onClose={handleCancelRevoke}
        isLoading={isSaving}
      >
        This will revoke the current API key immediately. The key will no longer be usable for API access.
      </AdminDialog>

      <Snackbar
        open={isCopySnackbarOpen}
        autoHideDuration={2500}
        onClose={handleCloseCopySnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message='API key copied to clipboard'
      />
    </section>
  );
};
